import { GenericDialog } from "src/components";
import { connect } from "react-redux";
import { addEditConfigurationDialogActions } from "./add-edit-configuration-dialog-store";
import { configuratorPageK, useConfiguratorPageT } from "src/translation-hooks/configurator-page";
import { Button, TooltipShortcut } from "@itk/mui-components";
import { ProductDetails, SelectProduct, SelectSystem } from "./components";
import { RootState } from "src/store";
import { useMemo, useState } from "react";
import { TRenderStep } from "./add-edit-configuration-dialog-types";
import { genericTranslationsK, useGenericTranslationsT } from "src/translation-hooks/generic-translations";
import { createGuid } from "@itk/typescript-utils";
import { useNavigate, useParams } from "react-router";
import { configureProductActions } from "src/pages/configure-product/configure-product-store";
import { ROUTES_URL } from "src/utils/constants";
import { TConfigureProductSearchParams } from "src/pages/configure-product/configure-product-types";
import { getProductFilterValidities } from "./components/select-product/product-filter-validity-utils";

const mapStateToProps = (state: RootState) => ({
  addEditConfigurationDialog: state.pageReducer.combinedConfiguratorReducer.addEditConfigurationDialogReducer.addEditConfigurationDialog,
  formData: state.pageReducer.combinedConfiguratorReducer.addEditConfigurationDialogReducer.formData,
  productsState: state.pageReducer.combinedConfiguratorReducer.addEditConfigurationDialogReducer.productsState,
  attributesState: state.pageReducer.combinedConfiguratorReducer.addEditConfigurationDialogReducer.attributesState
});

const ownActions = {
  getConfigurationRequest: configureProductActions.getConfigurationRequest,
  setSelectedProjectLineItemId: configureProductActions.setSelectedProjectLineItemId,
  setAddEditConfigurationDialog: addEditConfigurationDialogActions.setAddEditConfigurationDialog,
  createOrUpdateConfigurationRequest: addEditConfigurationDialogActions.createOrUpdateConfigurationRequest
};

type Props = typeof ownActions & ReturnType<typeof mapStateToProps>;

const AddEditConfigurationDialogBase = (props: Props) => {
  const {
    addEditConfigurationDialog,
    setAddEditConfigurationDialog,
    formData,
    createOrUpdateConfigurationRequest,
    getConfigurationRequest,
    setSelectedProjectLineItemId,
    productsState,
    attributesState
  } = props;

  const navigate = useNavigate();

  const { projectId = "", configurationId = "" } = useParams<{ projectId: string; configurationId: string }>();

  const [isLoading, setIsLoading] = useState(false);

  const configuratorPageT = useConfiguratorPageT();
  const genericTranslationsT = useGenericTranslationsT();

  const handleSave = (productId?: string) => {
    const systemProductConfigurationId = formData.systemProductConfigurationId;

    if (!formData.productsFilter || !formData.productsFilter.systemPackageId) return;
    // systemProductConfigurationItemId replaces projectLineItemId as the new item ID field
    const systemProductConfigurationItemId = createGuid();

    const product = productsState.loading === "has-loaded" ? productsState.data?.find(p => p.productId === (productId ?? formData.selectedProductId)) : undefined;

    setIsLoading(true);
    createOrUpdateConfigurationRequest(
      {
        systemProductConfigurationItemId,
        parentProjectLineItemId: formData?.productsFilter?.parentProjectLineItemId || null,
        systemPackageId: formData.productsFilter.systemPackageId,
        productId: productId ?? formData.selectedProductId,
        projectId,
        productTypeId: product?.productTypeId || "",
        description: product?.name || "",
        systemProductConfigurationId,
        attributeValues: formData.productsFilter.attributeFilters
      },
      (isSuccess, data) => {
        setIsLoading(false);
        if (isSuccess && data) {
          // if it has configuration id that means the dialog was opened on configure-product page
          if (configurationId) {
            getConfigurationRequest(data.systemProductConfigurationId);
            setSelectedProjectLineItemId(systemProductConfigurationItemId);
          } else {
            navigate({
              pathname: ROUTES_URL.CONFIGURE_PRODUCT.build(projectId, data.systemProductConfigurationId),
              search:
                data.systemProductConfigurationItemId !== systemProductConfigurationItemId
                  ? `?${new URLSearchParams({ projectLineItemId: systemProductConfigurationItemId } as TConfigureProductSearchParams).toString()}`
                  : ""
            });
          }
          setAddEditConfigurationDialog(undefined);
        }
      }
    );
  };

  // mirrors select-product's own validity check so the footer save-button stays in sync with the required-filter validation
  // across every matched filter attribute (there can be more than one List-type attribute scoped to the same product type)
  const filterValidities = useMemo(() => getProductFilterValidities(attributesState, formData.productsFilter?.attributeFilters), [attributesState, formData.productsFilter?.attributeFilters]);
  const isProductFilterValid = filterValidities.every(v => v.isValid);

  const renderStep = useMemo((): TRenderStep => {
    switch (addEditConfigurationDialog?.step) {
      case "SELECT SYSTEM":
        return {
          dialogTitle: configuratorPageT.selectSystem,
          component: <SelectSystem onSave={() => setAddEditConfigurationDialog({ step: "SELECT PRODUCT" })} />,
          saveAndCancelButton: {
            saveDisabled: !formData.productsFilter?.systemPackageId,
            saveLabel: "selectSystem",
            saveFn() {
              setAddEditConfigurationDialog({ step: "SELECT PRODUCT" });
            },
            cancelLabel: "cancel",
            cancelFn() {
              setAddEditConfigurationDialog(undefined);
            }
          }
        };
      case "SELECT PRODUCT":
        return {
          dialogTitle: configuratorPageT.selectProduct,
          component: <SelectProduct onSave={handleSave} />,
          saveAndCancelButton: {
            // also require the required-filter attribute to have a valid selected value before allowing save
            saveDisabled: !formData.selectedProductId || !isProductFilterValid,
            saveLabel: "selectProductAndSave",
            saveFn() {
              handleSave();
            },
            cancelLabel: formData.productsFilter?.parentProjectLineItemId ? "cancel" : "back",
            cancelFn() {
              if (formData.productsFilter?.parentProjectLineItemId) {
                setAddEditConfigurationDialog(undefined);
              } else {
                setAddEditConfigurationDialog({ step: "SELECT SYSTEM" });
              }
            }
          }
        };
      case "PRODUCT-DETAILS":
        return {
          dialogTitle: configuratorPageT.selectProduct,
          component: <ProductDetails />,
          saveAndCancelButton: {
            saveDisabled: false,
            saveLabel: "selectProductAndSave",
            saveFn() {
              handleSave();
            },
            cancelLabel: "back",
            cancelFn() {
              setAddEditConfigurationDialog({ step: "SELECT PRODUCT" });
            }
          },
          onClose: () => setAddEditConfigurationDialog({ step: "SELECT PRODUCT" })
        };
    }
  }, [addEditConfigurationDialog?.step, configuratorPageT, formData, isProductFilterValid]);

  if (!renderStep) return null;

  return (
    <GenericDialog
      dialogProps={{
        maxWidth: "lg"
      }}
      dialogContentProps={{
        sx: {
          height: "80vh"
        }
      }}
      headerTitle={renderStep.dialogTitle}
      onClose={() => {
        if (isLoading) return;
        renderStep.onClose ? renderStep.onClose() : setAddEditConfigurationDialog(undefined);
      }}
      renderDialogActions={
        <>
          <TooltipShortcut shortcut={{ action: renderStep.saveAndCancelButton.cancelFn, shortCutKey: "a", modifier: "alt" }}>
            <Button
              disabled={isLoading}
              t={genericTranslationsT}
              k={genericTranslationsK}
              field={renderStep.saveAndCancelButton.cancelLabel}
              onClick={renderStep.saveAndCancelButton.cancelFn}
              variant="outlined"
            />
          </TooltipShortcut>
          <TooltipShortcut shortcut={{ action: renderStep.saveAndCancelButton.saveFn, shortCutKey: "g", modifier: "alt" }}>
            <Button
              t={configuratorPageT}
              k={configuratorPageK}
              field={renderStep.saveAndCancelButton.saveLabel}
              onClick={renderStep.saveAndCancelButton.saveFn}
              variant="contained"
              disabled={renderStep.saveAndCancelButton.saveDisabled || isLoading}
            />
          </TooltipShortcut>
        </>
      }
    >
      {renderStep.component}
    </GenericDialog>
  );
};

export const AddEditConfigurationDialog = connect(mapStateToProps, ownActions)(AddEditConfigurationDialogBase);
